// The Hiring Request's fields (lane B2 S08): his required request fields (G §5) in five cards, the level V1 still
// requires to pin the guides, and one fieldset of the V1 fields he has not named (./compat.ts). One component draws the
// HR Business Partner's draft and the Hiring Manager's corrections; in `view` mode the same cards show values only, every
// label once and in the case every field reads (F-51: the level is a field, the org card carries no second Org alignment).
// "HRBP creates request and drafts justification/data; app routes complete request + current JD to HM."
// B13 (DC-234): the Timing and budget card after Headcount approval (F-143), and the Current JD as a full-width band in
// the centre after Justification and the business-unit fields, on the edit form and the read-only view (F-144) — the
// request page has no side column.

import type { ReactNode } from "react";
import {
  ACCESS_LABELS, Card, CheckTile, COMPENSATION_REVIEW_STATUS, Field, Flex, Grid, Input, LEVEL_DISPLAY, LevelGroup, PersonPick, ROLE_LABELS,
  SCORECARD_LABELS, Segmented, Select, Stack, TextArea, formatDate, type Level, type PersonOption,
} from "@/mc";
import { COMP_FIELD_LABELS } from "../compensation/labels";
import { REQ_RULES } from "../requisition/labels";
import { range } from "../requisition/data";
import { BRAND_CHOICES, V1_FIELD_WORDS, type RequestDraft } from "./compat";
import { POSTING_SCOPE_LABELS as S, REQUEST_FIELD_LABELS as F, REQUEST_PAGE_LABELS as P, TIMING_LABELS as T } from "./labels";

export const POSTING_SCOPE_WORD: Readonly<Record<string, string>> = {
  internal_only: S.internalOnly,
  external: S.external,
  confidential: S.confidential,
};

export const NEW_REPLACEMENT_WORD: Readonly<Record<string, string>> = {
  new_headcount: F.newHeadcount,
  backfill: F.replacement,
};

/** Headcount approval in his access words: funded → Yes, pending → Pending, unfunded → No. */
/** Urgency in his words (G §5 *Urgency · Normal / Urgent*). */
export const URGENCY_WORD: Readonly<Record<string, string>> = {
  normal: T.normal,
  urgent: T.urgent,
};

/** The target compensation range's currencies (codes, not words); the record's own is kept when it is another. */
const CURRENCIES = ["USD", "CAD"] as const;

export const HEADCOUNT_WORD: Readonly<Record<string, string>> = {
  funded: ACCESS_LABELS.Yes,
  pending: COMPENSATION_REVIEW_STATUS.pending,
  unfunded: ACCESS_LABELS.No,
};

export interface RequestFieldsProps {
  draft: RequestDraft;
  onChange: (next: RequestDraft) => void;
  mode: "edit" | "view";
  /** The Hiring Managers the originator may name (absent: the HM is shown, not chosen). */
  hmOptions?: readonly PersonOption[] | undefined;
  hmName?: string | undefined;
  /** Legacy Admin only: the originating HR Business Partner. */
  hrbpOptions?: readonly PersonOption[] | undefined;
  /** Job family / area of interest are required only while their taxonomy has an active entry (server). */
  taxonomyRequired?: { job_family: boolean; area_of_interest: boolean } | undefined;
  /** The target compensation range: editable, read-only (the HM's certification: not among its corrections), or not
   * drawn (the field policy did not send it to this seat). Default: as `mode`. */
  compMode?: "edit" | "view" | "hidden" | undefined;
  /** A slot under the cards (the corrections' Diff and Reason). */
  footer?: ReactNode;
}

export function RequestFields({ draft, onChange, mode, hmOptions, hmName, hrbpOptions, taxonomyRequired, compMode, footer }: RequestFieldsProps) {
  const set = <K extends keyof RequestDraft>(key: K, value: RequestDraft[K]) => onChange({ ...draft, [key]: value });
  const view = mode === "view";
  const backfill = draft.reason === "backfill";
  const text = (key: keyof RequestDraft, label: string, hs: string, opts: { area?: boolean; required?: boolean; mono?: boolean; type?: string } = {}) => (
    <Field label={label} required={opts.required ?? true} mode={mode}>
      {opts.area ? (
        <TextArea value={String(draft[key] ?? "")} onChange={(event) => set(key, event.target.value as never)} hs={hs} rows={3} />
      ) : (
        <Input value={String(draft[key] ?? "")} onChange={(event) => set(key, event.target.value as never)} hs={hs} mono={opts.mono} type={opts.type} />
      )}
    </Field>
  );

  return (
    <Stack gap={16}>
      <Card id="req-card-title">
        <Stack gap={16}>
        <Grid cols={2} min={260}>
          {text("title", F.title, "req.title")}
          {hrbpOptions ? (
            <Field label={ROLE_LABELS.hrbpAbbr} required mode={mode}>
              <PersonPick options={hrbpOptions} value={draft.requester_id} onChange={(value) => set("requester_id", value)} label={ROLE_LABELS.hrbpAbbr}  />
            </Field>
          ) : null}
          <Field label={ROLE_LABELS.hmAbbr} required mode={hmOptions && !view ? "edit" : "view"}>
            {hmOptions && !view ? (
              <PersonPick options={hmOptions} value={draft.hiring_manager_id} onChange={(value) => set("hiring_manager_id", value)} label={ROLE_LABELS.hmAbbr}  />
            ) : (
              <Input value={hmName ?? ""} readOnly />
            )}
          </Field>
          <Field label={F.newReplacement} required mode={mode} id="req-new-replacement">
            {view ? (
              <Input value={NEW_REPLACEMENT_WORD[draft.reason] ?? ""} readOnly />
            ) : (
              <Segmented
                aria-labelledby="req-new-replacement-l"
                value={draft.reason || "new_headcount"}
                options={[
                  { value: "new_headcount", label: F.newHeadcount, hs: "req.new-replacement.new" },
                  { value: "backfill", label: F.replacement, hs: "req.new-replacement.replacement" },
                ]}
                onChange={(value) => set("reason", value as RequestDraft["reason"])}
              />
            )}
          </Field>
          {backfill ? text("vacating_person_ref", F.replacementEmployee, "req.replacement-employee") : null}
          {text("location", F.location, "req.location")}
          {text("openings_requested", F.openings, "req.openings", { type: "number" })}
          {text("department", F.department, "req.department")}
          <Field label={F.postingType} required mode={mode}>
            {view ? (
              <Input value={POSTING_SCOPE_WORD[draft.posting_scope] ?? ""} readOnly />
            ) : (
              <Select
                value={draft.posting_scope}
                options={(["internal_only", "external", "confidential"] as const).map((value) => ({ value, label: POSTING_SCOPE_WORD[value]! }))}
                onChange={(value) => set("posting_scope", value as RequestDraft["posting_scope"])}
                
              />
            )}
          </Field>
          {/* F-169: an explicit Yes / No, unanswered until chosen (the Headcount approval's chip pair); request.submit
              refuses an unanswered question, and the read-only view prints Yes, No or —. */}
          <Field label={F.peopleManager} required mode={mode} id="req-people-manager">
            {view ? (
              <Input value={draft.is_people_manager === null ? "" : draft.is_people_manager ? ACCESS_LABELS.Yes : ACCESS_LABELS.No} readOnly />
            ) : (
              <Segmented
                aria-labelledby="req-people-manager-l"
                value={draft.is_people_manager === null ? "" : draft.is_people_manager ? "yes" : "no"}
                options={[
                  { value: "yes", label: ACCESS_LABELS.Yes, hs: "req.people-manager.yes" },
                  { value: "no", label: ACCESS_LABELS.No, hs: "req.people-manager.no" },
                ]}
                onChange={(value) => set("is_people_manager", value === "yes")}
              />
            )}
          </Field>
          {draft.is_people_manager ? text("direct_reports", F.directReports, "req.direct-reports", { type: "number" }) : null}
        </Grid>
        {view ? (
          // a field like every other in view mode (F-51), not the level group's uppercase key
          <Field label={SCORECARD_LABELS.level} required mode={mode}>
            <Input value={draft.level in LEVEL_DISPLAY ? LEVEL_DISPLAY[draft.level as Level] : ""} readOnly />
          </Field>
        ) : (
          <LevelGroup value={(draft.level || null) as Level | null} onChange={(level) => set("level", level)} hs={{ manager_and_below: "req.level.manager", director_srdirector: "req.level.director", vp_and_above: "req.level.vp" }} />
        )}
        {text("notes", F.notes, "req.notes", { area: true })}
        </Stack>
      </Card>

      <Card id="req-card-org">
        <Grid cols={2} min={260}>
          {text("org_alignment", F.orgAlignment, "req.org-alignment")}
          {text("job_code", F.jobCode, "req.job-code", { mono: true })}
          {text("cost_center", F.costCenter, "req.cost-center", { mono: true })}
          {text("job_family", F.jobFamily, "req.job-family", { required: taxonomyRequired?.job_family ?? false })}
          {text("area_of_interest", F.areaOfInterest, "req.area-of-interest", { required: taxonomyRequired?.area_of_interest ?? false })}
        </Grid>
      </Card>

      <Card id="req-card-headcount">
        <Field label={F.headcountApproval} required mode={mode} id="req-headcount">
          {view ? (
            <Input value={HEADCOUNT_WORD[draft.budget_status] ?? ""} readOnly />
          ) : (
            <Segmented
              aria-labelledby="req-headcount-l"
              value={draft.budget_status || "pending"}
              options={(["funded", "pending", "unfunded"] as const).map((value) => ({ value, label: HEADCOUNT_WORD[value]!, hs: `req.headcount-approval.${value}` }))}
              onChange={(value) => set("budget_status", value as RequestDraft["budget_status"])}
            />
          )}
        </Field>
      </Card>

      <TimingCard draft={draft} set={set} mode={mode} compMode={compMode ?? mode} />

      <Card id="req-card-justification">
        {text("justification", F.justification, "req.justification", { area: true })}
      </Card>

      {/* QUESTION (S08 report): V1 still requires these; he has not named them (./compat.ts). */}
      <Card id="req-card-v1">
        <Grid cols={2} min={260}>
          {text("business_unit", V1_FIELD_WORDS.businessUnit, "req.business-unit")}
          <Field label={V1_FIELD_WORDS.brands} required mode={mode}>
            {view ? (
              <Input value={BRAND_CHOICES.find((choice) => choice.value === draft.brand)?.label ?? draft.brand} readOnly />
            ) : (
              <Select value={draft.brand} options={BRAND_CHOICES} onChange={(value) => set("brand", value)}  />
            )}
          </Field>
          {text("reports_to_title", V1_FIELD_WORDS.reportsToTitle, "req.reports-to-title")}
          {backfill ? text("vacating_last_day", V1_FIELD_WORDS.lastDay, "req.last-day", { type: "date" }) : null}
          {backfill ? (
            <Field label={V1_FIELD_WORDS.levelAndTitleUnchanged} required mode={mode}>
              {view ? (
                <Input value={draft.level_and_title_unchanged === null ? "" : draft.level_and_title_unchanged ? ACCESS_LABELS.Yes : ACCESS_LABELS.No} readOnly />
              ) : (
                <CheckTile checked={draft.level_and_title_unchanged === true} onChange={(checked) => set("level_and_title_unchanged", checked)} >
                  {V1_FIELD_WORDS.levelAndTitleUnchanged}
                </CheckTile>
              )}
            </Field>
          ) : null}
        </Grid>
      </Card>
      <CurrentJd value={draft.job_description} onChange={(value) => set("job_description", value)} mode={mode} />
      {footer}
    </Stack>
  );
}

/** Timing and budget (DC-234, F-143): Target start date (required to route), Urgency (Normal · Urgent, nothing
 * preselected on a new request), the optional Target compensation range (Min – Max and a currency; his sentence as its
 * hint) and the optional Position number. */
function TimingCard({ draft, set, mode, compMode }: {
  draft: RequestDraft; set: <K extends keyof RequestDraft>(key: K, value: RequestDraft[K]) => void; mode: "edit" | "view"; compMode: "edit" | "view" | "hidden";
}) {
  const view = mode === "view";
  const shownRange = range(draft.target_comp_min || null, draft.target_comp_max || null, draft.currency);
  const currencies = CURRENCIES.includes(draft.currency as (typeof CURRENCIES)[number]) || !draft.currency ? CURRENCIES : [...CURRENCIES, draft.currency];
  return (
    <Card id="req-card-timing" title={T.timingAndBudget}>
      <Grid cols={2} min={260}>
        <Field label={T.targetStartDate} required mode={mode} id="req-target-start">
          {view
            ? <Input value={draft.target_start ? formatDate(draft.target_start, "UTC") : ""} readOnly />
            : <Input type="date" value={draft.target_start} onChange={(event) => set("target_start", event.target.value)}  />}
        </Field>
        <Field label={T.urgency} required mode={mode} id="req-urgency">
          {view ? (
            <Input value={URGENCY_WORD[draft.urgency] ?? ""} readOnly />
          ) : (
            <Segmented
              aria-labelledby="req-urgency-l"
              value={draft.urgency}
              options={(["normal", "urgent"] as const).map((value) => ({ value, label: URGENCY_WORD[value]!, hs: `req.urgency.${value}` }))}
              onChange={(value) => set("urgency", value as RequestDraft["urgency"])}
            />
          )}
        </Field>
        {compMode === "hidden" ? null : compMode === "view" ? (
          <Field label={T.targetCompRange} mode="view">
            <Input value={shownRange === "—" ? "" : shownRange} readOnly />
          </Field>
        ) : (
          <Field label={T.targetCompRange} id="req-target-comp-min" hint={REQ_RULES.compensationAlone}>
            <Flex gap={8} align="center" wrap>
              <span className="mc-hint" id="req-target-comp-min-w">{COMP_FIELD_LABELS.min}</span>
              <Input
                value={draft.target_comp_min}
                onChange={(event) => set("target_comp_min", event.target.value)}
                inputMode="decimal"
                mono
                aria-labelledby="req-target-comp-min-l req-target-comp-min-w"
                
              />
              <span aria-hidden="true">–</span>
              <span className="mc-hint" id="req-target-comp-max-w">{COMP_FIELD_LABELS.max}</span>
              <Input
                id="req-target-comp-max"
                value={draft.target_comp_max}
                onChange={(event) => set("target_comp_max", event.target.value)}
                inputMode="decimal"
                mono
                aria-labelledby="req-target-comp-min-l req-target-comp-max-w"
                
              />
              <Select
                id="req-target-comp-currency"
                value={draft.currency || "USD"}
                options={currencies.map((code) => ({ value: code, label: code }))}
                onChange={(value) => set("currency", value)}
                aria-label={`${T.targetCompRange} ${COMP_FIELD_LABELS.currency}`}
                sm
                
              />
            </Flex>
          </Field>
        )}
        <Field label={T.positionNumber} mode={mode} id="req-position-no">
          <Input value={draft.position_no} onChange={(event) => set("position_no", event.target.value)} maxLength={64} mono  />
        </Field>
      </Grid>
    </Card>
  );
}

/** The Current JD: a full-width band in the centre (F-144), after Justification and the business-unit fields. */
export function CurrentJd({ value, onChange, mode }: { value: string; onChange: (value: string) => void; mode: "edit" | "view" }) {
  return (
    <Card id="req-card-jd">
      <Field label={P.currentJd} required mode={mode}>
        <TextArea value={value} onChange={(event) => onChange(event.target.value)} rows={14}  />
      </Field>
    </Card>
  );
}

/** The required fields a draft still lacks, as server column keys (the server stays the authority at Submit). */
export function missingOf(d: RequestDraft, taxonomyRequired: { job_family: boolean; area_of_interest: boolean }): string[] {
  const out: string[] = [];
  const need = (key: string, ok: boolean) => { if (!ok) out.push(key); };
  need("title", !!d.title.trim());
  need("intended_hm_id", !!d.hiring_manager_id);
  need("level", !!d.level);
  need("reason", !!d.reason);
  if (d.reason === "backfill") {
    need("vacating_person_ref", !!d.vacating_person_ref.trim());
    need("vacating_last_day", !!d.vacating_last_day);
    need("level_and_title_unchanged", d.level_and_title_unchanged !== null);
  }
  need("locations", !!d.location.trim());
  need("openings_requested", Number(d.openings_requested) >= 1);
  need("is_people_manager", d.is_people_manager !== null);
  if (d.is_people_manager) need("direct_reports", d.direct_reports.trim() !== "");
  need("posting_scope", !!d.posting_scope);
  need("notes", !!d.notes.trim());
  need("department", !!d.department.trim());
  if (taxonomyRequired.job_family) need("job_family", !!d.job_family.trim());
  if (taxonomyRequired.area_of_interest) need("area_of_interest", !!d.area_of_interest.trim());
  need("job_code", !!d.job_code.trim());
  need("org_alignment", !!d.org_alignment.trim());
  need("budget_status", !!d.budget_status);
  need("cost_center", !!d.cost_center.trim());
  need("justification", !!d.justification.trim());
  need("job_description", !!d.job_description.trim());
  need("target_start", !!d.target_start);
  need("urgency", !!d.urgency);
  need("business_unit", !!d.business_unit.trim());
  need("brands", !!d.brand);
  need("reports_to_title", !!d.reports_to_title.trim());
  return out;
}
